import React, { useState, useEffect } from 'react';
import { UserCog, Plus, Trash2, Shield, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const ROLE_STYLE = { superadmin:'bg-red-100 text-red-700', admin:'bg-blue-100 text-blue-700', editor:'bg-amber-100 text-amber-700' };

export default function AdminUsers() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]         = useState({ name:'', email:'', password:'', role:'editor' });
  const [saving, setSaving]     = useState(false);
  const [showPw, setShowPw]     = useState(false);
  const [msg, setMsg]           = useState('');
  const { admin } = useAuth();

  const fetchUsers = () => {
    setLoading(true);
    api.get('/auth/admins').then(r => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      await api.post('/auth/create', form);
      setMsg('success');
      fetchUsers();
      setTimeout(() => { setShowModal(false); setMsg(''); setForm({ name:'', email:'', password:'', role:'editor' }); }, 1200);
    } catch (err) { setMsg('error:' + (err.response?.data?.message || 'Failed to create admin')); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (id === admin?.id) return alert("You can't delete your own account.");
    if (!window.confirm('Delete this admin? This cannot be undone.')) return;
    await api.delete(`/auth/admins/${id}`);
    fetchUsers();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCog size={22} className="text-royal-800" /> Admin Users
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">{users.length} admin accounts</p>
        </div>
        {admin?.role === 'superadmin' && (
          <button onClick={() => { setShowModal(true); setMsg(''); }} className="btn-royal text-sm py-2.5 px-5 flex items-center gap-2">
            <Plus size={16} /> Add Admin
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 gap-2 text-gray-400">
            <Loader2 size={20} className="animate-spin" /> Loading...
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                {['Admin','Email','Role','Status','Joined',''].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(u => (
                <tr key={u._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-royal-800 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-semibold text-gray-900">{u.name}</span>
                      {u._id === admin?.id && <span className="px-2 py-0.5 bg-royal-50 text-royal-700 rounded-full text-[10px] font-bold">You</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-600 text-sm">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${ROLE_STYLE[u.role] || 'bg-gray-100 text-gray-600'}`}>
                      <Shield size={10} /> {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${u.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-400">{new Date(u.createdAt).toLocaleDateString('en-NG')}</td>
                  <td className="px-5 py-3">
                    {admin?.role === 'superadmin' && u._id !== admin?.id && (
                      <button onClick={() => del(u._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-display font-semibold text-gray-900">Create Admin User</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">✕</button>
            </div>

            {msg.startsWith('error:') && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{msg.slice(6)}</div>
            )}
            {msg === 'success' && (
              <div className="mx-6 mt-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-xl px-4 py-3">✅ Admin created successfully</div>
            )}

            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {[{k:'name',l:'Full Name',t:'text'},{k:'email',l:'Email Address',t:'email'}].map(f => (
                <div key={f.k}>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{f.l} *</label>
                  <input type={f.t} required value={form[f.k]} onChange={e => setForm(p => ({...p,[f.k]:e.target.value}))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password *</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} required value={form.password} onChange={e => setForm(p => ({...p,password:e.target.value}))}
                    className="w-full pl-3 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPw ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Role</label>
                <select value={form.role} onChange={e => setForm(p => ({...p,role:e.target.value}))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white">
                  <option value="editor">Editor — can manage content</option>
                  <option value="admin">Admin — full access except user management</option>
                  <option value="superadmin">Super Admin — complete access</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-2.5 bg-royal-800 hover:bg-royal-900 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {saving ? <><Loader2 size={14} className="animate-spin"/>Creating...</> : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
